import { Button, useAppForm } from '@stern-ui/ui';
import { useStore } from '@tanstack/react-form';
import { format } from 'date-fns';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { z } from 'zod';

import { DialogContainer as Dialog } from '@/components/Elements';
import { addNotification } from '@/components/Notifications/notificationsSlice';
import { useCreateContactLogMutation } from '@/features/patientDetails';
import { CreateContactLogRequest } from '@/features/patientEngagement';
import { trackEvent } from '@/lib/analytics';
import { contactMethodOptions } from '@/utils/filterOptions';

// Dialog form for logging a new contact with a patient. Comment and outcome are required unless the
// contact method is a follow up; a call date is required when the outcome is a call back.

const FOLLOW_UP = 'follow_up';
const CALL_BACK = 'call_back';

const contactOutcomeOptions = [
	{ label: 'Call Back', value: CALL_BACK },
	{ label: 'Successful', value: 'successful' },
	{ label: 'Unsuccessful', value: 'unsuccessful' },
];

const createContactSchema = z
	.object({
		patient: z.string(),
		contact_method: z.string().min(1, 'Contact method is required'),
		comment: z.string(),
		outcome: z.string(),
		scheduled_at: z.iso.date('Please enter a valid date').or(z.literal('')),
	})
	.superRefine((values, ctx) => {
		if (values.contact_method !== FOLLOW_UP) {
			if (!values.comment.trim()) {
				ctx.addIssue({ code: 'custom', path: ['comment'], message: 'Comment is required' });
			}
			if (!values.outcome) {
				ctx.addIssue({ code: 'custom', path: ['outcome'], message: 'Outcome is required' });
			}
		}
		if (values.outcome === CALL_BACK && !values.scheduled_at) {
			ctx.addIssue({
				code: 'custom',
				path: ['scheduled_at'],
				message: 'Schedule call date is required',
			});
		}
		// yyyy-MM-dd strings compare correctly as text, and avoid timezone issues with Date objects
		if (values.scheduled_at && values.scheduled_at < format(new Date(), 'yyyy-MM-dd')) {
			ctx.addIssue({
				code: 'custom',
				path: ['scheduled_at'],
				message: 'Date cannot be in the past',
			});
		}
	});

type CreateNewContactFormProps = {
	close: () => void;
	isOpen: boolean;
	patient_identifier: string;
	setDoInvalidateTagsOnClose?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const CreateNewContactForm = (props: CreateNewContactFormProps) => {
	const { close, isOpen, patient_identifier, setDoInvalidateTagsOnClose } = props;
	const dispatch = useDispatch();
	const [createContactLog, { isSuccess }] = useCreateContactLogMutation();

	const defaultValues: CreateContactLogRequest = {
		patient: patient_identifier,
		contact_method: '',
		comment: '',
		outcome: '',
		scheduled_at: '',
	};

	useEffect(() => {
		if (isSuccess) {
			dispatch(
				addNotification({
					type: 'success',
					title: 'Success',
					message: 'You have successfully created a new contact',
					timeoutLength: 8000,
				})
			);
			setDoInvalidateTagsOnClose && setDoInvalidateTagsOnClose(true);
		}
	}, [dispatch, isSuccess, setDoInvalidateTagsOnClose]);

	const form = useAppForm({
		defaultValues,
		validators: {
			onMount: createContactSchema,
			onChange: createContactSchema,
		},
		onSubmit: async ({ value }) => {
			await createContactLog(value);
			trackEvent('New patient contact created', {
				contactMethod: value.contact_method,
				outcome: value.outcome,
			});
			close();
		},
	});

	const contactMethod = useStore(form.store, (state) => state.values.contact_method);
	const outcome = useStore(form.store, (state) => state.values.outcome);
	const isFollowUp = contactMethod === FOLLOW_UP;
	const showScheduledCall = isFollowUp || outcome === CALL_BACK;

	// clear a field that has just been hidden, so a stale value isn't submitted
	const clearField = (name: 'comment' | 'outcome' | 'scheduled_at') => {
		form.setFieldValue(name, '');
	};

	return (
		<Dialog
			close={close}
			isOpen={isOpen}
			title='Create new contact'
			className='w-full max-w-lg'
		>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					void form.handleSubmit();
				}}
				data-test-id='create-new-contact-form'
			>
				<div className='mt-6 grid grid-cols-1 gap-4'>
					<form.AppField
						name='contact_method'
						listeners={{
							onChange: ({ value }) => {
								if (value === FOLLOW_UP) {
									clearField('comment');
									clearField('outcome');
								}
							},
						}}
					>
						{(field) => (
							<field.SelectField
								label='Contact Method'
								options={contactMethodOptions}
								required
							/>
						)}
					</form.AppField>
					{!isFollowUp && (
						<div>
							<form.AppField name='comment'>
								{(field) => (
									<field.TextAreaField label='Comment' rows={8} required />
								)}
							</form.AppField>
						</div>
					)}
				</div>
				<div className='mt-6 grid grid-cols-2 gap-4'>
					{!isFollowUp && (
						<form.AppField
							name='outcome'
							listeners={{
								onChange: ({ value }) => {
									if (value !== CALL_BACK) clearField('scheduled_at');
								},
							}}
						>
							{(field) => (
								<field.SelectField
									label='Outcome'
									options={contactOutcomeOptions}
									required
								/>
							)}
						</form.AppField>
					)}
					{showScheduledCall && (
						<div>
							<form.AppField name='scheduled_at'>
								{(field) => (
									<field.TextField
										label='Schedule Call'
										type='date'
										required={outcome === CALL_BACK}
										min={new Date().toISOString().split('T')[0]}
									/>
								)}
							</form.AppField>
						</div>
					)}
				</div>
				<div className='flex flex-row justify-between pt-5'>
					<Button variant='tertiary' onClick={close}>
						Close
					</Button>
					<form.AppForm>
						<form.SubmitButton dataTestId='create-new-contact-submit-button'>
							Submit
						</form.SubmitButton>
					</form.AppForm>
				</div>
			</form>
		</Dialog>
	);
};
